import React from 'react';
import { Link } from 'react-router-dom';
import { FaHome, FaChevronRight, FaDesktop, FaLaptop, FaTv, FaCheckCircle, FaArrowRight, FaPhone, FaCashRegister, FaBoxes, FaTools } from 'react-icons/fa';
import Navigation from '../components/Navigation';
import TopBar from '../components/TopBar';
import Footer from '../components/Footer';

const HardwareDeploymentPage = () => {
    const services = [
        {
            icon: <FaDesktop size={24} />,
            title: 'Workstation Deployment',
            description: 'Large-scale desktop and laptop rollouts with imaging and configuration.',
            gradient: 'from-purple-500 to-violet-500'
        },
        {
            icon: <FaCashRegister size={24} />,
            title: 'POS Systems',
            description: 'Point-of-sale hardware installation for retail and hospitality.',
            gradient: 'from-blue-600 to-cyan-600'
        },
        {
            icon: <FaTv size={24} />,
            title: 'Digital Signage',
            description: 'Display and digital signage installations for any environment.',
            gradient: 'from-orange-500 to-red-500'
        },
        {
            icon: <FaBoxes size={24} />,
            title: 'Kiosk Installation',
            description: 'Self-service kiosks and interactive terminals deployment.',
            gradient: 'from-green-500 to-teal-500'
        },
        {
            icon: <FaLaptop size={24} />,
            title: 'Mobile Devices',
            description: 'Tablets, handheld devices, and mobile equipment deployment.',
            gradient: 'from-pink-500 to-rose-500'
        },
        {
            icon: <FaTools size={24} />,
            title: 'Installation Services',
            description: 'Professional mounting, cabling, and configuration services.',
            gradient: 'from-amber-500 to-orange-500'
        }
    ];

    const capabilities = [
        { number: '10K+', label: 'Devices Deployed', description: 'Annually' },
        { number: '106+', label: 'Locations', description: 'Served' },
        { number: '24hr', label: 'Turnaround', description: 'Available' },
        { number: '99%', label: 'Success Rate', description: 'First-time' }
    ];

    return (
        <div className="font-sans">
            <TopBar />
            <Navigation />

            {/* Hero Section */}
            <section className="relative bg-gradient-to-br from-gray-900 via-purple-900 to-violet-900 py-24 overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    }}></div>
                </div>

                <div className="absolute top-0 right-0 w-96 h-96 bg-purple-500 rounded-full filter blur-3xl opacity-20 translate-x-1/2 -translate-y-1/2"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-violet-500 rounded-full filter blur-3xl opacity-20 -translate-x-1/2 translate-y-1/2"></div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <nav className="flex items-center gap-2 text-sm text-gray-300 mb-8">
                        <Link to="/" className="flex items-center gap-1 hover:text-white transition-colors">
                            <FaHome size={14} />
                            <span>Home</span>
                        </Link>
                        <FaChevronRight size={10} className="text-gray-500" />
                        <Link to="/services/national-rollouts" className="hover:text-white transition-colors">National Rollouts</Link>
                        <FaChevronRight size={10} className="text-gray-500" />
                        <span className="text-purple-400">Hardware Deployment</span>
                    </nav>

                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-violet-400">Hardware</span> Deployment
                            </h1>
                            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                                Large-scale hardware rollouts including kiosks, POS systems, digital signage, and more for multi-site deployments.
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-purple-500 to-violet-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-purple-500/30 transition-all duration-300">
                                    Start Deployment
                                    <FaArrowRight size={14} />
                                </Link>
                                <a href="tel:+12014099845" className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/30 text-white rounded-xl font-semibold hover:bg-white/10 transition-all duration-300">
                                    <FaPhone size={14} />
                                    Call Us Now
                                </a>
                            </div>
                        </div>
                        <div className="relative">
                            <div className="bg-gradient-to-br from-purple-600/20 to-violet-600/20 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
                                <img
                                    src="https://images.unsplash.com/photo-1531973576160-7125cd663d86?w=600&h=400&fit=crop"
                                    alt="Hardware Deployment"
                                    className="w-full h-80 object-cover rounded-xl shadow-2xl"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white border-b border-gray-100">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        {capabilities.map((stat, index) => (
                            <div key={index} className="text-center">
                                <div className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-violet-500 mb-2">
                                    {stat.number}
                                </div>
                                <div className="text-xl font-semibold text-gray-900">{stat.label}</div>
                                <div className="text-sm text-gray-500">{stat.description}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <span className="inline-block px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold mb-4">Our Services</span>
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Hardware We Deploy</h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            From workstations to kiosks, we handle all types of hardware deployments
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {services.map((service, index) => (
                            <div
                                key={index}
                                className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-purple-200"
                            >
                                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${service.gradient} flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform duration-300`}>
                                    {service.icon}
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                                <p className="text-gray-600 leading-relaxed">{service.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Process Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-purple-50">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <img
                                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=500&fit=crop"
                                alt="Deployment Process"
                                className="w-full h-auto rounded-2xl shadow-2xl"
                            />
                        </div>
                        <div>
                            <span className="inline-block px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold mb-4">Our Process</span>
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Streamlined Deployment</h2>
                            <p className="text-lg text-gray-600 mb-8">
                                Our proven deployment process ensures successful rollouts with minimal disruption.
                            </p>
                            <div className="space-y-4">
                                {['Pre-deployment staging and testing', 'Coordinated scheduling across locations', 'Professional on-site installation', 'Complete configuration and testing', 'Asset documentation and tracking', 'Post-deployment support'].map((item, index) => (
                                    <div key={index} className="flex items-center gap-3">
                                        <FaCheckCircle className="text-purple-500 flex-shrink-0" size={20} />
                                        <span className="text-gray-700 font-medium">{item}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-purple-600 to-violet-600">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Ready to Deploy Hardware?</h2>
                    <p className="text-xl text-purple-100 mb-8">
                        Get a customized deployment plan for your multi-site rollout.
                    </p>
                    <div className="flex flex-wrap justify-center gap-4">
                        <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-white text-purple-600 rounded-xl font-semibold hover:shadow-lg transition-all duration-300">
                            Request Quote
                            <FaArrowRight size={14} />
                        </Link>
                        <a href="tel:+12014099845" className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white rounded-xl font-semibold hover:bg-white/10 transition-all duration-300">
                            <FaPhone size={14} />
                            Call Now
                        </a>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default HardwareDeploymentPage;
